import { useState, useMemo, FC } from "react";
import Button from "components/Button";
import {
  ModalContent,
  ModalSections,
  ModalButtonsSection,
} from "components/Modal";
import { useAnalyticsFilters } from "providers/AnalyticsProvider";
import { useAnalyticsFilterOptions } from "hooks/analytics/useAnalyticsFilterOptions";
import {
  ALL_PRODUCT_IDS,
  PRODUCT_DISPLAY_NAMES,
  MAX_DOMAINS_SELECTION,
} from "utils/analytics";
import ComboboxSelect from "components/ComboboxSelect";
import { LoadingOverlay, ParameterCheckboxGroup } from "./shared";

export const FiltersModalContent: FC<{ closeModalCallback: () => void }> = ({
  closeModalCallback,
}) => {
  const { filters, setFilters, dateRange } = useAnalyticsFilters();

  const baseFilters = useMemo(() => ({
    productType: filters.productType || "All",
    parameters: filters.parameters || [],
    domains: filters.domains || [],
    countries: filters.countries || [],
  }), [filters]);

  const [productType, setProductType] = useState(baseFilters.productType);
  const [parameters, setParameters] = useState<string[]>(baseFilters.parameters);
  const [domains, setDomains] = useState<string[]>(baseFilters.domains);
  const [countries, setCountries] = useState<string[]>(baseFilters.countries);

  const currentFilters = useMemo(
    () => ({ productType, parameters, domains, countries }),
    [productType, parameters, domains, countries],
  );

  const {
    allProductTypes,
    availableOptions,
    isLoading,
    handleDomainSearch,
  } = useAnalyticsFilterOptions(dateRange, currentFilters, setDomains, setCountries);

  const handleSave = () => {
    setFilters({
      productType: productType === "All" ? null : productType,
      parameters,
      domains,
      countries,
    });
    closeModalCallback();
  };

  // The modal unmounts on close, which drops the draft and aborts its requests
  const handleCancel = () => {
    closeModalCallback();
  };

  const handleReset = () => {
    setProductType("All");
    setParameters([]);
    setDomains([]);
    setCountries([]);
  };

  const productTypeOptions = [
    { name: "All", value: "All" },
    ...ALL_PRODUCT_IDS
      .filter((id) => allProductTypes.includes(id))
      .map((id) => ({
        name: PRODUCT_DISPLAY_NAMES[id] || id,
        value: id,
      })),
  ];

  return (
    <ModalContent smallerGapFromButtons>
      <ModalSections>
        <div className="space-y-6 relative">
          <LoadingOverlay isLoading={isLoading} />

          <ComboboxSelect
            label="Product type"
            tooltipMessage="Select the product type to narrow down results."
            multiple={false}
            value={productType}
            options={productTypeOptions}
            onChange={setProductType}
            disabled={isLoading}
          />

          <ParameterCheckboxGroup
            parameters={parameters}
            availableParameters={availableOptions.parameters}
            onChange={setParameters}
            disabled={isLoading}
          />

          <ComboboxSelect
            label="Domains"
            tooltipMessage={`Search and select up to ${MAX_DOMAINS_SELECTION} domains to filter analytics data.`}
            options={availableOptions.domains}
            selected={domains}
            onChange={setDomains}
            disabled={isLoading}
            loading={isLoading}
            searchPlaceholder="Search domains..."
            maxSelection={MAX_DOMAINS_SELECTION}
            onSearch={handleDomainSearch}
          />

          <ComboboxSelect
            label="Locations"
            options={availableOptions.countries}
            selected={countries}
            onChange={setCountries}
            disabled={isLoading}
            loading={isLoading}
            enableSearch={false}
          />
        </div>
      </ModalSections>

      <ModalButtonsSection justifyBetween="justify-between">
        <Button
          text="Reset"
          onClick={handleReset}
          size="MD"
          className="button button-secondary"
          disabled={isLoading}
        />
        <div className="flex gap-2">
          <Button
            text="Cancel"
            onClick={handleCancel}
            size="MD"
            className="button button-tertiary"
          />
          <Button
            text="Save"
            onClick={handleSave}
            size="MD"
            className="button button-primary"
            disabled={isLoading}
          />
        </div>
      </ModalButtonsSection>
    </ModalContent>
  );
};
